import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI, moduloAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import {
    Users,
    Key,
    ArrowLeft,
    Shield,
    UserCog,
    Loader2,
    Eye,
    EyeOff,
    UserPlus,
    Trash2,
    Search,
    Phone,
    Mail,
    CreditCard,
    Pencil,
} from 'lucide-react';

const ROLES_DISPONIBLES = [
    { value: 'admin', label: 'Administrador' },
    { value: 'gerente', label: 'Gerente / Aprobador' },
    { value: 'solicitante', label: 'Solicitante' },
    { value: 'seguridad', label: 'Proteccion Industrial' },
];

const emptyForm = {
    nombre: '',
    email: '',
    rut: '',
    telefono: '',
    rol: '',
    password: '',
    modulos_acceso: [],
};

export default function GestionUsuariosPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [usuarios, setUsuarios] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');

    // Create user
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [createForm, setCreateForm] = useState({ ...emptyForm });
    const [creating, setCreating] = useState(false);
    const [showCreatePassword, setShowCreatePassword] = useState(false);
    const [modulosEmpresa, setModulosEmpresa] = useState([]);

    // Change password
    const [selectedUser, setSelectedUser] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [saving, setSaving] = useState(false);

    // Change own password
    const [ownPasswordDialog, setOwnPasswordDialog] = useState(false);
    const [currentPassword, setCurrentPassword] = useState('');
    const [ownNewPassword, setOwnNewPassword] = useState('');
    const [ownConfirmPassword, setOwnConfirmPassword] = useState('');

    // Delete confirmation
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    // Edit user
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editForm, setEditForm] = useState({ nombre: '', rut: '', telefono: '', rol: '', modulos_acceso: [] });
    const [editingUser, setEditingUser] = useState(null);
    const [editSaving, setEditSaving] = useState(false);

    useEffect(() => {
        loadUsuarios();
        loadModulosEmpresa();
    }, []);

    const loadUsuarios = async () => {
        setLoading(true);
        try {
            const res = await authAPI.getUsuarios();
            setUsuarios(res.data);
        } catch (err) {
            console.error('Error loading usuarios:', err);
            toast.error('Error al cargar usuarios');
        } finally {
            setLoading(false);
        }
    };

    const loadModulosEmpresa = async () => {
        try {
            const res = await moduloAPI.getEmpresa();
            setModulosEmpresa(res.data);
        } catch (err) {
            console.error('Error loading modulos:', err);
        }
    };

    // ── Create User ──
    const handleOpenCreate = () => {
        setCreateForm({ ...emptyForm });
        setShowCreatePassword(false);
        setCreateDialogOpen(true);
    };

    const handleCreateUser = async () => {
        const { nombre, email, rol, password } = createForm;
        if (!nombre.trim()) return toast.error('El nombre es obligatorio');
        if (!email.trim()) return toast.error('El email es obligatorio');
        if (!rol) return toast.error('Seleccione un rol');
        if (!password || password.length < 6) return toast.error('La contraseña debe tener al menos 6 caracteres');

        setCreating(true);
        try {
            await authAPI.createUsuario({
                nombre: nombre.trim(),
                email: email.trim().toLowerCase(),
                rut: createForm.rut.trim() || null,
                telefono: createForm.telefono.trim() || null,
                rol,
                password,
                modulos_acceso: createForm.modulos_acceso,
            });
            toast.success(`Usuario ${nombre} creado exitosamente`);
            setCreateDialogOpen(false);
            loadUsuarios();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al crear usuario');
        } finally {
            setCreating(false);
        }
    };

    const toggleModuloAcceso = (codigo) => {
        setCreateForm(prev => {
            const current = prev.modulos_acceso;
            const next = current.includes(codigo)
                ? current.filter(c => c !== codigo)
                : [...current, codigo];
            return { ...prev, modulos_acceso: next };
        });
    };

    // ── Edit User ──
    const handleOpenEdit = (usuario) => {
        setEditingUser(usuario);
        setEditForm({
            nombre: usuario.nombre || '',
            rut: usuario.rut || '',
            telefono: usuario.telefono || '',
            rol: usuario.rol || '',
            modulos_acceso: usuario.modulos_acceso || [],
        });
        setEditDialogOpen(true);
    };

    const toggleEditModulo = (codigo) => {
        setEditForm(prev => {
            const current = prev.modulos_acceso;
            const next = current.includes(codigo)
                ? current.filter(c => c !== codigo)
                : [...current, codigo];
            return { ...prev, modulos_acceso: next };
        });
    };

    const handleSaveEdit = async () => {
        if (!editForm.nombre.trim()) return toast.error('El nombre es obligatorio');
        if (!editForm.rol) return toast.error('Seleccione un rol');
        setEditSaving(true);
        try {
            await authAPI.updateUsuario(editingUser.id, {
                nombre: editForm.nombre.trim(),
                rut: editForm.rut.trim() || null,
                telefono: editForm.telefono.trim() || null,
                rol: editForm.rol,
                modulos_acceso: editForm.modulos_acceso,
            });
            toast.success(`Usuario ${editForm.nombre} actualizado`);
            setEditDialogOpen(false);
            loadUsuarios();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al actualizar usuario');
        } finally {
            setEditSaving(false);
        }
    };

    const canEditUser = (targetUser) => {
        if (targetUser.rol === 'superadmin' && user.rol !== 'superadmin') return false;
        if (user.rol === 'superadmin') return true;
        if (user.rol === 'admin') return targetUser.empresa_id === user.empresa_id;
        return false;
    };

    // ── Change Password ──
    const handleOpenChangePassword = (usuario) => {
        setSelectedUser(usuario);
        setNewPassword('');
        setConfirmPassword('');
        setShowPassword(false);
        setDialogOpen(true);
    };

    const handleChangePassword = async () => {
        if (!newPassword || newPassword.length < 6) {
            toast.error('La contraseña debe tener al menos 6 caracteres');
            return;
        }
        if (newPassword !== confirmPassword) {
            toast.error('Las contraseñas no coinciden');
            return;
        }
        setSaving(true);
        try {
            await authAPI.changePassword(selectedUser.id, newPassword);
            toast.success(`Contraseña de ${selectedUser.nombre} actualizada`);
            setDialogOpen(false);
            setSelectedUser(null);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al cambiar contraseña');
        } finally {
            setSaving(false);
        }
    };

    const handleChangeOwnPassword = async () => {
        if (!ownNewPassword || ownNewPassword.length < 6) {
            toast.error('La contraseña debe tener al menos 6 caracteres');
            return;
        }
        if (ownNewPassword !== ownConfirmPassword) {
            toast.error('Las contraseñas no coinciden');
            return;
        }
        setSaving(true);
        try {
            await authAPI.changeOwnPassword(currentPassword, ownNewPassword);
            toast.success('Tu contraseña ha sido actualizada');
            setOwnPasswordDialog(false);
            setCurrentPassword('');
            setOwnNewPassword('');
            setOwnConfirmPassword('');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al cambiar contraseña');
        } finally {
            setSaving(false);
        }
    };

    // ── Delete User ──
    const handleConfirmDelete = (usuario) => {
        setUserToDelete(usuario);
        setDeleteDialogOpen(true);
    };

    const handleDeleteUser = async () => {
        if (!userToDelete) return;
        setDeleting(true);
        try {
            await authAPI.deleteUsuario(userToDelete.id);
            toast.success(`Usuario ${userToDelete.nombre} eliminado`);
            setDeleteDialogOpen(false);
            setUserToDelete(null);
            loadUsuarios();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al eliminar usuario');
        } finally {
            setDeleting(false);
        }
    };

    // ── Helpers ──
    const getRolBadgeColor = (rol) => {
        switch (rol) {
            case 'superadmin': return 'bg-red-500 text-white';
            case 'admin': return 'bg-cyan-600 text-white';
            case 'gerente': return 'bg-blue-500 text-white';
            case 'seguridad': return 'bg-green-500 text-white';
            case 'solicitante': return 'bg-gray-500 text-white';
            default: return 'bg-gray-500 text-white';
        }
    };

    const canChangePassword = (targetUser) => {
        if (user.rol === 'superadmin') return true;
        if (user.rol === 'admin') {
            if (targetUser.rol === 'superadmin') return false;
            return targetUser.empresa_id === user.empresa_id;
        }
        return false;
    };

    const canDeleteUser = (targetUser) => {
        if (targetUser.id === user.id) return false;
        if (targetUser.rol === 'superadmin') return false;
        if (user.rol === 'superadmin') return true;
        if (user.rol === 'admin') return targetUser.empresa_id === user.empresa_id;
        return false;
    };

    const filteredUsuarios = usuarios.filter(u => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return (
            u.nombre?.toLowerCase().includes(term) ||
            u.email?.toLowerCase().includes(term) ||
            u.rut?.toLowerCase().includes(term) ||
            u.rol?.toLowerCase().includes(term)
        );
    });

    if (loading) {
        return (
            <div className="space-y-6 p-6">
                <Skeleton className="h-10 w-64" />
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }

    return (
        <div className="space-y-6 p-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/home')} data-testid="back-btn">
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <UserCog className="h-6 w-6" />
                            Gestion de Usuarios
                        </h1>
                        <p className="text-sm text-muted-foreground">
                            {usuarios.length} usuario{usuarios.length !== 1 ? 's' : ''} registrado{usuarios.length !== 1 ? 's' : ''}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <Button variant="outline" onClick={() => setOwnPasswordDialog(true)} data-testid="change-own-password-btn">
                        <Key className="h-4 w-4 mr-2" />
                        Mi Clave
                    </Button>
                    <Button onClick={handleOpenCreate} data-testid="create-user-btn">
                        <UserPlus className="h-4 w-4 mr-2" />
                        Crear Usuario
                    </Button>
                </div>
            </div>

            {/* Search */}
            <div className="relative max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                    placeholder="Buscar por nombre, email, RUT..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="search-users-input"
                />
            </div>

            {/* Users Table */}
            <Card className="border-2">
                <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                        <Users className="h-5 w-5" />
                        Usuarios del Sistema
                    </CardTitle>
                    <CardDescription>
                        {user.rol === 'superadmin'
                            ? 'Todos los usuarios del sistema'
                            : 'Usuarios de tu empresa'}
                    </CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Nombre</TableHead>
                                <TableHead>Email</TableHead>
                                <TableHead>RUT</TableHead>
                                <TableHead>Rol</TableHead>
                                <TableHead className="text-right">Acciones</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredUsuarios.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                                        {searchTerm ? 'Sin resultados para la busqueda' : 'No hay usuarios registrados'}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredUsuarios.map((u) => (
                                    <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium shrink-0">
                                                    {u.nombre?.charAt(0) || 'U'}
                                                </div>
                                                <div className="min-w-0">
                                                    <span className="font-medium block truncate">{u.nombre}</span>
                                                    {u.telefono && (
                                                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                                                            <Phone className="h-3 w-3" />{u.telefono}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell className="font-mono text-sm">{u.email}</TableCell>
                                        <TableCell className="text-sm text-muted-foreground">{u.rut || '-'}</TableCell>
                                        <TableCell>
                                            <Badge className={`${getRolBadgeColor(u.rol)} capitalize`}>
                                                {u.rol === 'superadmin' && <Shield className="h-3 w-3 mr-1" />}
                                                {u.rol}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                {canEditUser(u) && (
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => handleOpenEdit(u)}
                                                        data-testid={`edit-user-${u.email}`}
                                                    >
                                                        <Pencil className="h-4 w-4 mr-1" />
                                                        Editar
                                                    </Button>
                                                )}
                                                {canChangePassword(u) && (
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => handleOpenChangePassword(u)}
                                                        data-testid={`change-password-${u.email}`}
                                                    >
                                                        <Key className="h-4 w-4 mr-1" />
                                                        Clave
                                                    </Button>
                                                )}
                                                {canDeleteUser(u) && (
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                        onClick={() => handleConfirmDelete(u)}
                                                        data-testid={`delete-user-${u.email}`}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {/* ═══ CREATE USER DIALOG ═══ */}
            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <UserPlus className="h-5 w-5" />
                            Crear Nuevo Usuario
                        </DialogTitle>
                        <DialogDescription>
                            Completa los datos para registrar un nuevo usuario en el sistema
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        {/* Nombre */}
                        <div className="space-y-1.5">
                            <Label htmlFor="create-nombre">Nombre completo *</Label>
                            <Input
                                id="create-nombre"
                                value={createForm.nombre}
                                onChange={(e) => setCreateForm(p => ({ ...p, nombre: e.target.value }))}
                                placeholder="Juan Perez"
                                data-testid="create-nombre-input"
                            />
                        </div>

                        {/* Email */}
                        <div className="space-y-1.5">
                            <Label htmlFor="create-email">
                                <Mail className="h-3.5 w-3.5 inline mr-1" />Email *
                            </Label>
                            <Input
                                id="create-email"
                                type="email"
                                value={createForm.email}
                                onChange={(e) => setCreateForm(p => ({ ...p, email: e.target.value }))}
                                placeholder="usuario@empresa.cl"
                                data-testid="create-email-input"
                            />
                        </div>

                        {/* RUT + Telefono row */}
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label htmlFor="create-rut">
                                    <CreditCard className="h-3.5 w-3.5 inline mr-1" />RUT
                                </Label>
                                <Input
                                    id="create-rut"
                                    value={createForm.rut}
                                    onChange={(e) => setCreateForm(p => ({ ...p, rut: e.target.value }))}
                                    placeholder="12.345.678-9"
                                    data-testid="create-rut-input"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="create-telefono">
                                    <Phone className="h-3.5 w-3.5 inline mr-1" />Telefono
                                </Label>
                                <Input
                                    id="create-telefono"
                                    value={createForm.telefono}
                                    onChange={(e) => setCreateForm(p => ({ ...p, telefono: e.target.value }))}
                                    placeholder="+56 9 1234 5678"
                                    data-testid="create-telefono-input"
                                />
                            </div>
                        </div>

                        {/* Rol */}
                        <div className="space-y-1.5">
                            <Label>Rol *</Label>
                            <Select
                                value={createForm.rol}
                                onValueChange={(val) => setCreateForm(p => ({ ...p, rol: val }))}
                            >
                                <SelectTrigger data-testid="create-rol-select">
                                    <SelectValue placeholder="Seleccionar rol" />
                                </SelectTrigger>
                                <SelectContent>
                                    {ROLES_DISPONIBLES.map(r => (
                                        <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Modulos */}
                        {modulosEmpresa.length > 0 && (
                            <div className="space-y-1.5">
                                <Label>Modulos de Acceso</Label>
                                <div className="grid grid-cols-1 gap-2 p-3 border rounded-md bg-muted/30">
                                    {modulosEmpresa.map(m => (
                                        <label
                                            key={m.codigo}
                                            className="flex items-center gap-2 cursor-pointer text-sm"
                                            data-testid={`modulo-check-${m.codigo}`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={createForm.modulos_acceso.includes(m.codigo)}
                                                onChange={() => toggleModuloAcceso(m.codigo)}
                                                className="rounded border-gray-300"
                                            />
                                            <span>{m.nombre}</span>
                                        </label>
                                    ))}
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    Si no selecciona ninguno, el usuario tendra acceso a todos los modulos asignados a la empresa
                                </p>
                            </div>
                        )}

                        {/* Password */}
                        <div className="space-y-1.5">
                            <Label htmlFor="create-password">Contraseña *</Label>
                            <div className="relative">
                                <Input
                                    id="create-password"
                                    type={showCreatePassword ? 'text' : 'password'}
                                    value={createForm.password}
                                    onChange={(e) => setCreateForm(p => ({ ...p, password: e.target.value }))}
                                    placeholder="Minimo 6 caracteres"
                                    data-testid="create-password-input"
                                />
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="absolute right-0 top-0 h-full px-3"
                                    onClick={() => setShowCreatePassword(!showCreatePassword)}
                                    tabIndex={-1}
                                >
                                    {showCreatePassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </Button>
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                            Cancelar
                        </Button>
                        <Button onClick={handleCreateUser} disabled={creating} data-testid="save-create-user-btn">
                            {creating ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            ) : (
                                <UserPlus className="h-4 w-4 mr-2" />
                            )}
                            Crear Usuario
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ═══ CHANGE PASSWORD DIALOG ═══ */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Cambiar Contraseña</DialogTitle>
                        <DialogDescription>
                            Ingresa la nueva contraseña para {selectedUser?.nombre}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="new-password">Nueva Contraseña</Label>
                            <div className="relative">
                                <Input
                                    id="new-password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    placeholder="Minimo 6 caracteres"
                                    data-testid="new-password-input"
                                />
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="absolute right-0 top-0 h-full px-3"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </Button>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="confirm-password">Confirmar Contraseña</Label>
                            <Input
                                id="confirm-password"
                                type={showPassword ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Repite la contraseña"
                                data-testid="confirm-password-input"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>
                            Cancelar
                        </Button>
                        <Button onClick={handleChangePassword} disabled={saving} data-testid="save-password-btn">
                            {saving ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            ) : (
                                <Key className="h-4 w-4 mr-2" />
                            )}
                            Guardar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ═══ CHANGE OWN PASSWORD DIALOG ═══ */}
            <Dialog open={ownPasswordDialog} onOpenChange={setOwnPasswordDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Cambiar Mi Contraseña</DialogTitle>
                        <DialogDescription>
                            Ingresa tu contraseña actual y la nueva contraseña
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="current-password">Contraseña Actual</Label>
                            <Input
                                id="current-password"
                                type="password"
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                placeholder="Tu contraseña actual"
                                data-testid="current-password-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="own-new-password">Nueva Contraseña</Label>
                            <Input
                                id="own-new-password"
                                type="password"
                                value={ownNewPassword}
                                onChange={(e) => setOwnNewPassword(e.target.value)}
                                placeholder="Minimo 6 caracteres"
                                data-testid="own-new-password-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="own-confirm-password">Confirmar Nueva Contraseña</Label>
                            <Input
                                id="own-confirm-password"
                                type="password"
                                value={ownConfirmPassword}
                                onChange={(e) => setOwnConfirmPassword(e.target.value)}
                                placeholder="Repite la nueva contraseña"
                                data-testid="own-confirm-password-input"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOwnPasswordDialog(false)}>
                            Cancelar
                        </Button>
                        <Button onClick={handleChangeOwnPassword} disabled={saving} data-testid="save-own-password-btn">
                            {saving ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            ) : (
                                <Key className="h-4 w-4 mr-2" />
                            )}
                            Cambiar Contraseña
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ═══ DELETE CONFIRMATION DIALOG ═══ */}
            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Eliminar Usuario</DialogTitle>
                        <DialogDescription>
                            ¿Estas seguro de eliminar a <strong>{userToDelete?.nombre}</strong> ({userToDelete?.email})?
                            Esta accion no se puede deshacer.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                            Cancelar
                        </Button>
                        <Button variant="destructive" onClick={handleDeleteUser} disabled={deleting} data-testid="confirm-delete-user-btn">
                            {deleting ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            ) : (
                                <Trash2 className="h-4 w-4 mr-2" />
                            )}
                            Eliminar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ═══ EDIT USER DIALOG ═══ */}
            <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Pencil className="h-5 w-5" />
                            Editar Usuario
                        </DialogTitle>
                        <DialogDescription>
                            {editingUser?.email}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="space-y-1.5">
                            <Label>Nombre completo</Label>
                            <Input
                                value={editForm.nombre}
                                onChange={(e) => setEditForm(p => ({ ...p, nombre: e.target.value }))}
                                data-testid="edit-nombre-input"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label><CreditCard className="h-3.5 w-3.5 inline mr-1" />RUT</Label>
                                <Input
                                    value={editForm.rut}
                                    onChange={(e) => setEditForm(p => ({ ...p, rut: e.target.value }))}
                                    data-testid="edit-rut-input"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label><Phone className="h-3.5 w-3.5 inline mr-1" />Telefono</Label>
                                <Input
                                    value={editForm.telefono}
                                    onChange={(e) => setEditForm(p => ({ ...p, telefono: e.target.value }))}
                                    data-testid="edit-telefono-input"
                                />
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Rol</Label>
                            <Select
                                value={editForm.rol}
                                onValueChange={(val) => setEditForm(p => ({ ...p, rol: val }))}
                            >
                                <SelectTrigger data-testid="edit-rol-select">
                                    <SelectValue placeholder="Seleccionar rol" />
                                </SelectTrigger>
                                <SelectContent>
                                    {ROLES_DISPONIBLES.map(r => (
                                        <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        {modulosEmpresa.length > 0 && (
                            <div className="space-y-1.5">
                                <Label>Modulos de Acceso</Label>
                                <div className="grid grid-cols-1 gap-2 p-3 border rounded-md bg-muted/30">
                                    {modulosEmpresa.map(m => (
                                        <label
                                            key={m.codigo}
                                            className="flex items-center gap-2 cursor-pointer text-sm"
                                            data-testid={`edit-modulo-check-${m.codigo}`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={editForm.modulos_acceso.includes(m.codigo)}
                                                onChange={() => toggleEditModulo(m.codigo)}
                                                className="rounded border-gray-300"
                                            />
                                            <span>{m.nombre}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                            Cancelar
                        </Button>
                        <Button onClick={handleSaveEdit} disabled={editSaving} data-testid="save-edit-user-btn">
                            {editSaving ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            ) : (
                                <Pencil className="h-4 w-4 mr-2" />
                            )}
                            Guardar Cambios
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
